'use client';

import { useEffect, useRef, useState } from 'react';
import { agentAvatar } from '@/components/admin/cmo/agent-persona';

/**
 * Founder avatar for the support widget.
 *
 * Order: an admin-configured DiceBear avatar → an explicitly configured
 * headshot URL → the locked Tosh DiceBear avatar (`TOSH_AVATAR` in
 * agent-persona.ts), so the bubble always shows a polished face.
 *
 * This USED to default to `/founder/headshot.jpg`, a file that was never
 * uploaded. Every render, for every user, therefore requested a URL that 404s
 * and then fell back — invisible in the UI but a wasted request on each page,
 * and enough repeat 404s that crawlers logged it as a broken asset.
 *
 * To use a real photo: drop it at public/founder/headshot.jpg and set
 * NEXT_PUBLIC_FOUNDER_HEADSHOT_URL=/founder/headshot.jpg. The opt-in is
 * deliberate — the default must never point at a file that may not exist.
 *
 * Robustness: the SSR-rendered <img> can 404 BEFORE React hydrates + attaches
 * onError (the error event is then missed), which would leave a broken image
 * forever. We also check `naturalWidth===0` on mount to catch that race. Still
 * needed — a configured DiceBear URL or headshot can fail at any time.
 */
const FALLBACK_AVATAR = agentAvatar('tosh');

/** Opt-in real photo. Unset by default so we never request a missing file. */
const HEADSHOT_URL = process.env.NEXT_PUBLIC_FOUNDER_HEADSHOT_URL || null;

export interface FounderAvatarChoice {
  style: string;
  seed: string;
}

export function FounderAvatar({
  size = 36,
  name = 'Tosh',
  avatar,
  className,
}: {
  size?: number;
  name?: string;
  /** When set, render this DiceBear avatar instead of the headshot/default. */
  avatar?: FounderAvatarChoice | null;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const ref = useRef<HTMLImageElement>(null);

  const configured = avatar
    ? `https://api.dicebear.com/9.x/${avatar.style}/svg?seed=${encodeURIComponent(avatar.seed)}`
    : null;

  const primary = configured ?? HEADSHOT_URL ?? FALLBACK_AVATAR;
  const src = failed ? FALLBACK_AVATAR : primary;

  // Catch a load failure that fired before hydration attached onError (the
  // SSR'd headshot 404 race) — if the current image is already broken, fall back.
  useEffect(() => {
    const el = ref.current;

    if (el && el.complete && el.naturalWidth === 0) setFailed(true);
  }, [src]);

  return (
    // eslint-disable-next-line @next/next/no-img-element -- static / DiceBear avatar, no script execution
    <img
      ref={ref}
      src={src}
      alt={name}
      width={size}
      height={size}
      onError={() => setFailed(true)}
      className={`rounded-full object-cover shrink-0${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size }}
    />
  );
}
